/**
 * Recortar a foto do signatário na própria tela (E80.F1.T07 — ADR-0079 §2.2).
 *
 * O vocabulário visual é o do recorte de assinatura do wizard — caixa sobre a
 * imagem e giro em passos de 90° (ADR-0073) —, mas **não** é o
 * `DocumentPageCanvas`: aquele fala de página de documento e de bbox de
 * assinatura, e traz o Fabric inteiro junto. Aqui há uma imagem e uma caixa.
 *
 * O que sai daqui é GEOMETRIA: `bounding_box` normalizado em [0,1] e o giro. O
 * arquivo original não é tocado — é por isso que o editor desenha sempre sobre
 * a versão `original` da imagem. Desenhar sobre a efetiva mediria a caixa nova
 * contra o recorte anterior, e cada ajuste encolheria a foto um pouco mais.
 *
 * O arrasto usa eventos de MOUSE, e não de ponteiro: a mesa do perito é
 * desktop, e o `PointerEvent` não existe no jsdom — o teste do arrasto ficaria
 * sem como medir a caixa, que é justamente o que ele precisa provar.
 *
 * **Dois espaços de coordenadas** (E80.F1.C01, F-E80.F1-01). O estado guarda a
 * caixa em coordenadas do ORIGINAL — é o que o servidor grava e o que ele
 * recorta antes de girar. O desenho e o retângulo na tela vivem no espaço da
 * imagem EXIBIDA, que é a girada. As duas traduções são do `recorte_geometria`,
 * e é por causa delas que girar não desloca mais a seleção: a caixa continua
 * ancorada à mesma região do rosto.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RotateCw } from 'lucide-react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  baixarFotoDoSignatario,
  type CaixaDoRecorte,
  type FotoDoSignatarioData,
  type RotacaoDaFoto,
} from '@/lib/fotos_do_signatario';
import { medidasDoPalco, paraExibicao, paraOriginal } from '@/lib/recorte_geometria';

const t = ptBR.exameEletronico.fotosDoSignatario.recorte;

const ROTACOES: readonly RotacaoDaFoto[] = [0, 90, 180, 270];

/** Caixa menor que isto (fração do lado) é clique acidental, não recorte. */
const AREA_MINIMA = 0.01;

/** Maior lado que o palco ocupa na tela. O restante sai da proporção da foto. */
const LADO_MAXIMO_PX = 320;

function proxima(rotacao: RotacaoDaFoto): RotacaoDaFoto {
  return ROTACOES[(ROTACOES.indexOf(rotacao) + 1) % ROTACOES.length] ?? 0;
}

function normalizar(
  inicio: { x: number; y: number },
  fim: { x: number; y: number },
): CaixaDoRecorte {
  const x = Math.min(inicio.x, fim.x);
  const y = Math.min(inicio.y, fim.y);
  return {
    x,
    y,
    w: Math.min(Math.abs(fim.x - inicio.x), 1 - x),
    h: Math.min(Math.abs(fim.y - inicio.y), 1 - y),
  };
}

/**
 * A caixa do original vista pelo giro atual, ou `null` quando não há seleção.
 *
 * Caixa degenerada (largura ou altura zerada) conta como ausência: é o clique
 * sem arrasto, e desenhar um retângulo de um pixel só confundiria.
 */
function caixaNaTela(caixa: CaixaDoRecorte | null, rotacao: RotacaoDaFoto): CaixaDoRecorte | null {
  if (caixa === null || caixa.w <= 0 || caixa.h <= 0) return null;
  return paraExibicao(caixa, rotacao);
}

function pontoNaImagem(elemento: HTMLElement, clientX: number, clientY: number) {
  const caixa = elemento.getBoundingClientRect();
  const largura = caixa.width || 1;
  const altura = caixa.height || 1;
  return {
    x: Math.min(Math.max((clientX - caixa.left) / largura, 0), 1),
    y: Math.min(Math.max((clientY - caixa.top) / altura, 0), 1),
  };
}

/** Baixa a versão ORIGINAL da foto e devolve uma URL local pronta para `<img>`. */
function useOriginal(caseId: string, fotoId: string): { url: string | null; falhou: boolean } {
  const client = useApiClient();
  const [url, setUrl] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let cancelado = false;
    let criada: string | null = null;
    void (async () => {
      try {
        const blob = await baixarFotoDoSignatario(client, caseId, fotoId, 'original');
        if (cancelado) return;
        criada = URL.createObjectURL(blob);
        setUrl(criada);
      } catch {
        if (!cancelado) setFalhou(true);
      }
    })();
    return () => {
      cancelado = true;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, fotoId]);

  return { url, falhou };
}

interface PalcoProps {
  url: string;
  alt: string;
  rotacao: RotacaoDaFoto;
  /** A caixa NO ESPAÇO DA EXIBIÇÃO — já traduzida pelo giro atual. */
  caixaVisual: CaixaDoRecorte | null;
  medidas: ReturnType<typeof medidasDoPalco> | null;
  aoMedirImagem: (natural: { largura: number; altura: number }) => void;
  aoComecar: (evento: React.MouseEvent<HTMLDivElement>) => void;
  aoMover: (evento: React.MouseEvent<HTMLDivElement>) => void;
  aoSoltar: () => void;
  palcoRef: React.RefObject<HTMLDivElement>;
}

/**
 * A imagem e o retângulo desenhado sobre ela.
 *
 * Componente próprio porque as duas medidas condicionais (antes e depois de a
 * imagem carregar) faziam a função de cima passar do limite de complexidade —
 * e porque aqui a regra é uma só: **o palco tem a forma da imagem girada**, e
 * é contra ele que o arrasto e o retângulo são medidos.
 */
function PalcoDoRecorte({
  url,
  alt,
  rotacao,
  caixaVisual,
  medidas,
  aoMedirImagem,
  aoComecar,
  aoMover,
  aoSoltar,
  palcoRef,
}: PalcoProps): JSX.Element {
  const estiloDoPalco =
    medidas === null
      ? undefined
      : { width: `${medidas.palco.largura}px`, height: `${medidas.palco.altura}px` };
  // Antes de carregar não há medida natural: a imagem fica no fluxo, com o giro
  // aplicado, e o palco acompanha o tamanho dela.
  const estiloDaImagem =
    medidas === null
      ? { transform: `rotate(${rotacao}deg)` }
      : {
          width: `${medidas.imagem.largura}px`,
          height: `${medidas.imagem.altura}px`,
          transform: `translate(-50%, -50%) rotate(${rotacao}deg)`,
        };

  return (
    // Gesto de mouse sem semântica interativa nativa, no mesmo padrão já
    // justificado do `ZoomPanViewport` e do `AnnotationLayer`. O teclado não
    // fica sem caminho: o recorte é OPCIONAL por contrato (anexar a foto já
    // recortada é caminho de primeira classe, ADR-0079 §2.2), e girar, aplicar,
    // usar a foto inteira e cancelar são botões.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={palcoRef}
      className="relative inline-block max-w-full cursor-crosshair select-none overflow-hidden border border-border"
      role="application"
      aria-label={t.palcoLabel}
      data-testid="recorte-palco"
      style={estiloDoPalco}
      onMouseDown={aoComecar}
      onMouseMove={aoMover}
      onMouseUp={aoSoltar}
      // Soltar o botão fora da imagem encerra o arrasto onde ele parou — sem
      // isto, o ponteiro voltaria "desenhando" sem ninguém ter pedido.
      onMouseLeave={aoSoltar}
    >
      <img
        src={url}
        alt={alt}
        className={
          medidas === null ? 'block max-h-[320px] w-auto' : 'absolute left-1/2 top-1/2 max-w-none'
        }
        style={estiloDaImagem}
        data-testid="recorte-imagem"
        draggable={false}
        onLoad={(evento) =>
          aoMedirImagem({
            largura: evento.currentTarget.naturalWidth,
            altura: evento.currentTarget.naturalHeight,
          })
        }
      />
      {caixaVisual !== null ? (
        <div
          aria-hidden
          data-testid="recorte-caixa"
          className="pointer-events-none absolute border-2 border-accent bg-accent/20"
          style={{
            left: `${caixaVisual.x * 100}%`,
            top: `${caixaVisual.y * 100}%`,
            width: `${caixaVisual.w * 100}%`,
            height: `${caixaVisual.h * 100}%`,
          }}
        />
      ) : null}
    </div>
  );
}

export interface RecorteDaFotoProps {
  caseId: string;
  foto: FotoDoSignatarioData;
  salvando: boolean;
  onAplicar: (caixa: CaixaDoRecorte | null, rotacao: RotacaoDaFoto) => void;
  onCancelar: () => void;
}

export function RecorteDaFoto({
  caseId,
  foto,
  salvando,
  onAplicar,
  onCancelar,
}: RecorteDaFotoProps): JSX.Element {
  const { url, falhou } = useOriginal(caseId, foto.id);
  const palcoRef = useRef<HTMLDivElement>(null);
  const inicioRef = useRef<{ x: number; y: number } | null>(null);
  // Estado no espaço do ORIGINAL: é o que o servidor grava, e o que mantém a
  // seleção sobre a mesma região do rosto quando o giro muda.
  const [caixa, setCaixa] = useState<CaixaDoRecorte | null>(foto.bounding_box);
  const [rotacao, setRotacao] = useState<RotacaoDaFoto>(foto.rotacao_deg);
  const [natural, setNatural] = useState<{ largura: number; altura: number } | null>(null);

  const aoComecar = useCallback((evento: React.MouseEvent<HTMLDivElement>) => {
    const palco = palcoRef.current;
    if (palco === null) return;
    evento.preventDefault();
    inicioRef.current = pontoNaImagem(palco, evento.clientX, evento.clientY);
    setCaixa(null);
  }, []);

  const aoMover = useCallback(
    (evento: React.MouseEvent<HTMLDivElement>) => {
      const palco = palcoRef.current;
      if (palco === null || inicioRef.current === null) return;
      const exibida = normalizar(
        inicioRef.current,
        pontoNaImagem(palco, evento.clientX, evento.clientY),
      );
      setCaixa(paraOriginal(exibida, rotacao));
    },
    [rotacao],
  );

  const aoSoltar = useCallback(() => {
    inicioRef.current = null;
    // Clique sem arrasto não é recorte: descarta a caixa degenerada em vez de
    // gravar um enquadramento de um pixel que ninguém quis.
    setCaixa((atual) => (atual !== null && atual.w * atual.h < AREA_MINIMA ? null : atual));
  }, []);

  const caixaVisual = caixaNaTela(caixa, rotacao);
  const temCaixa = caixaVisual !== null;
  const medidas = natural !== null ? medidasDoPalco(natural, rotacao, LADO_MAXIMO_PX) : null;

  return (
    <div className="space-y-sc-3" data-testid="recorte-da-foto" data-rotacao={rotacao}>
      <div>
        <h4 className="text-body-md text-fg">{t.titulo.replace('{nome}', foto.nome_arquivo)}</h4>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      {falhou ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="recorte-indisponivel">
          {t.indisponivel}
        </p>
      ) : url === null ? (
        <p className="text-body-sm text-fg-dim">{t.carregando}</p>
      ) : (
        <PalcoDoRecorte
          url={url}
          alt={foto.nome_arquivo}
          rotacao={rotacao}
          caixaVisual={caixaVisual}
          medidas={medidas}
          palcoRef={palcoRef}
          aoMedirImagem={setNatural}
          aoComecar={aoComecar}
          aoMover={aoMover}
          aoSoltar={aoSoltar}
        />
      )}

      <p className="text-caption text-fg-dim" data-testid="recorte-estado">
        {temCaixa ? t.orientacao.replace('{deg}', String(rotacao)) : t.semCaixa}
      </p>

      <div className="flex flex-wrap items-center gap-sc-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setRotacao(proxima)}
          data-testid="recorte-girar"
        >
          <RotateCw className="h-4 w-4" aria-hidden />
          {t.girar}
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!temCaixa || salvando}
          onClick={() => onAplicar(caixa, rotacao)}
          data-testid="recorte-aplicar"
        >
          {salvando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t.aplicando}
            </>
          ) : (
            t.aplicar
          )}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={salvando}
          onClick={() => onAplicar(null, 0)}
          data-testid="recorte-desfazer"
        >
          {t.desfazer}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={salvando}
          onClick={onCancelar}
          data-testid="recorte-cancelar"
        >
          {t.cancelar}
        </Button>
      </div>
    </div>
  );
}
